/**
 * Upcoming tours — date-grouped cards, role filter, desktop hover preview.
 */
class ProductTable {
  constructor(root) {
    this.section = root;
    this.preview = root.querySelector('.product-table__preview');
    this.currentTrip = null;
    this.isDesktop = window.matchMedia('(min-width: 750px)').matches;

    this.#setupFilters();
    this.#setupFilterScroll();
    this.#setupDesktopDetails();
    this.#setupWetravelLinks();
    this.#setupPreview();
    this.#setupResize();
  }

  #setupFilters() {
    const buttons = this.section.querySelectorAll('[data-role-filter]');
    const trips = this.section.querySelectorAll('[data-product-table-trip]');

    for (const button of buttons) {
      button.addEventListener('click', () => {
        const role = button.getAttribute('data-role-filter');

        for (const other of buttons) {
          other.classList.toggle('is-active', other === button);
        }

        this.#scrollFilterIntoView(button);

        for (const trip of trips) {
          const values = trip.getAttribute('data-filters') || trip.getAttribute('data-roles') || '';
          const show = role === 'all' || values.split(',').includes(role);
          trip.classList.toggle('is-hidden', !show);
        }
      });
    }
  }

  #scrollFilterIntoView(button) {
    const scroller = this.filterScroller;

    if (!scroller || !scroller.contains(button)) return;

    const scrollerRect = scroller.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    const endPad = 28;
    const startPad = 8;

    if (buttonRect.right > scrollerRect.right - endPad) {
      scroller.scrollBy({ left: buttonRect.right - scrollerRect.right + endPad, behavior: 'smooth' });
    } else if (buttonRect.left < scrollerRect.left + startPad) {
      scroller.scrollBy({ left: buttonRect.left - scrollerRect.left - startPad, behavior: 'smooth' });
    }
  }

  #setupFilterScroll() {
    this.filterScroller = this.section.querySelector('.product-table__filters');

    if (!this.filterScroller) return;

    this.#updateFilterScroll();
    this.filterScroller.addEventListener('scroll', () => this.#updateFilterScroll(), { passive: true });
  }

  #updateFilterScroll() {
    const scroller = this.filterScroller;

    if (!scroller) return;

    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    const overflowing = maxScroll > 8;

    scroller.classList.toggle('is-scrolled', overflowing && scroller.scrollLeft > 8);
    scroller.classList.toggle('is-end', !overflowing || scroller.scrollLeft >= maxScroll - 8);
  }

  #setupDesktopDetails() {
    const details = this.section.querySelectorAll('.product-table__details');
    let isFirst = true;

    for (const item of details) {
      if (this.isDesktop) {
        item.open = true;
      } else {
        item.open = isFirst;
        isFirst = false;
      }
    }
  }

  #setupWetravelLinks() {
    const buttons = this.section.querySelectorAll('.product-table__wetravel .wtrvl-checkout_button');

    for (const button of buttons) {
      this.#resetWetravelButton(button);
    }
  }

  #resetWetravelButton(button) {
    button.removeAttribute('style');
    button.style.setProperty('height', 'var(--touch-target-size, var(--minimum-touch-target))', 'important');
    button.style.setProperty('min-height', 'var(--touch-target-size, var(--minimum-touch-target))', 'important');
    button.style.setProperty('max-height', 'var(--touch-target-size, var(--minimum-touch-target))', 'important');
    button.style.setProperty('padding', '0 16px', 'important');
    button.style.setProperty('font-size', '0.875rem', 'important');
    button.style.setProperty('line-height', '1', 'important');
    button.style.setProperty('font-family', 'inherit', 'important');
  }

  #setupPreview() {
    if (!this.preview) return;

    this.section.addEventListener('mouseenter', this.#onMouseEnter);
    this.section.addEventListener('mouseleave', this.#onMouseLeave);
    this.section.addEventListener('mousemove', this.#onMouseMove);
    this.section.addEventListener('focusin', this.#onFocusIn);
    this.section.addEventListener('focusout', this.#onFocusOut);

    const trips = this.section.querySelectorAll('[data-preview]');
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#preloadImage(entry.target);
          }
        }
      },
      { rootMargin: '50px', threshold: 0.1 }
    );

    for (const trip of trips) {
      this.intersectionObserver.observe(trip);
    }
  }

  #setupResize() {
    window.addEventListener(
      'resize',
      () => {
        const wasDesktop = this.isDesktop;
        this.isDesktop = window.matchMedia('(min-width: 750px)').matches;

        if (wasDesktop !== this.isDesktop) {
          this.#hidePreview();
          this.#setupDesktopDetails();
        }

        this.#updateFilterScroll();
      },
      { passive: true }
    );
  }

  #preloadImage(trip) {
    const src = trip.getAttribute('data-preview');
    if (!src || trip.dataset.imageLoaded) return;

    const img = new Image();
    img.onload = () => {
      trip.dataset.imageLoaded = 'true';
    };
    img.src = src;
  }

  #onMouseEnter = (event) => {
    if (!this.isDesktop) return;
    const trip = event.target.closest('[data-product-table-trip]');
    if (trip?.hasAttribute('data-preview')) {
      this.#showPreview(trip);
    }
  };

  #onMouseLeave = (event) => {
    if (!this.section.contains(event.relatedTarget)) {
      this.#hidePreview();
    }
  };

  #onMouseMove = (event) => {
    if (!this.isDesktop) return;

    const trip = event.target.closest('[data-product-table-trip]');
    if (trip?.hasAttribute('data-preview') && trip !== this.currentTrip) {
      this.#showPreview(trip);
    }

    if (this.currentTrip && this.preview) {
      this.#positionPreview(event.clientX, event.clientY);
    }
  };

  #onFocusIn = (event) => {
    const trip = event.target.closest('[data-product-table-trip]');
    if (trip?.hasAttribute('data-preview')) {
      this.#showPreview(trip);
    }
  };

  #onFocusOut = (event) => {
    if (!this.section.contains(event.relatedTarget)) {
      this.#hidePreview();
    }
  };

  #showPreview(trip) {
    if (!trip || trip === this.currentTrip || !this.isDesktop) return;

    this.currentTrip = trip;
    const previewImg = this.preview.querySelector('.product-table__preview-image');
    const src = trip.getAttribute('data-preview');
    const src2x = trip.getAttribute('data-preview-2x');

    if (!previewImg || !src) return;

    previewImg.src = src;
    if (src2x) {
      previewImg.srcset = `${src} 1x, ${src2x} 2x`;
    }
    previewImg.alt = '';
    this.preview.classList.add('is-visible');
  }

  #hidePreview() {
    this.currentTrip = null;
    this.preview?.classList.remove('is-visible');
  }

  #positionPreview(mouseX, mouseY) {
    if (!this.preview || !this.isDesktop) return;

    const sectionRect = this.section.getBoundingClientRect();
    const previewRect = this.preview.getBoundingClientRect();
    let left = mouseX - sectionRect.left - previewRect.width / 2;
    let top = mouseY - sectionRect.top - previewRect.height / 3;

    if (left < 0) left = 0;
    else if (left + previewRect.width > sectionRect.width) {
      left = sectionRect.width - previewRect.width;
    }

    if (top < 0) top = 0;
    else if (top + previewRect.height > sectionRect.height) {
      top = sectionRect.height - previewRect.height;
    }

    this.preview.style.transform = `translate3d(${left}px, ${top}px, 0)`;
  }

  destroy() {
    this.intersectionObserver?.disconnect();
  }
}

const initProductTables = (root = document) => {
  const sections = root.querySelectorAll('[data-product-table]');
  for (const section of sections) {
    if (section._productTableInstance) {
      section._productTableInstance.destroy();
    }
    section._productTableInstance = new ProductTable(section);
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initProductTables());
} else {
  initProductTables();
}

document.addEventListener('shopify:section:load', (event) => {
  initProductTables(event.target);
});
